import { DatePipe } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { CCAAModel } from '../../../pre-simulator/models/ccaa.model';
import { EuriborModel } from '../../models/euribor.model';
import { MortgageDataModel } from '../../models/mortgage-data.model';
import { SelectModel } from '../../models/select.model';

import { NavigationService } from '../../../shared/services/navigation/navigation.service';
import { CommonService } from '../../../vass-core/services/common.service';

import { CustomHttpParams } from '../../../vass-core/services/custom-http-params.class';
import { IS_PUBLIC_API } from '../../../vass-core/utils/is-public-api- context';

@Injectable()
export class MortgageService {
  private mortgagesConfigParams$: BehaviorSubject<SelectModel[]> = new BehaviorSubject(
    null,
  );

  private euribor: BehaviorSubject<EuriborModel> = new BehaviorSubject(undefined);

  public ccaa: Array<CCAAModel> = [];

  private postalCodeFinally: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private commonService: CommonService,
    private navigationService: NavigationService,
    private datePipe: DatePipe,
    private readonly http: HttpClient,
  ) {
    this.getMortgagesConfigParams();
    this.getEuriborRequest();
  }

  private getMortgagesConfigParams() {
    const url = `${environment.apiBase}/mortgages-config-params/${environment.api_version.config_params}/config`;
    return this.http
      .get<Array<SelectModel>>(url, {
        params: new CustomHttpParams(true),
        context: new HttpContext().set(IS_PUBLIC_API, true),
      })
      .pipe(
        map((resp) => this.sortArray(resp)),
        take(5),
        catchError((e) => []),
      )
      .subscribe(
        (response) => this.mortgagesConfigParams$.next(response),
        (error) => {
          this.navigationService.redirectToLayoutError(error);
          return throwError(error);
        },
      );
  }

  public getParamsByType(type: string) {
    return this.mortgagesConfigParams$.pipe(
      filter((list) => list !== null),
      map((list) => {
        const data = list.filter((e) => e.type === type);
        return data;
      }),
    );
  }

  simulateLevel2(updateSimulation: boolean = true): Observable<any> {
    let url: string;
    if (updateSimulation) {
      url = `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/simulation?update=true`;
    } else {
      url = `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/simulation?update=false`;
    }
    return this.commonService.getVerb(url).pipe(
      map((response) => {
        response.mortgageSimulateResponse.mortgageResult.calculation.ltv = parseFloat(
          response.mortgageSimulateResponse.mortgageResult.calculation.ltv,
        ).toFixed(3);
        if (
          response.mortgageSimulateResponse.reasons &&
          response.mortgageSimulateResponse.reasons.includes(
            'RULE_MORTGAGE_EDAD_MINIMA_TIT_MIN_INGR_REASON_KO',
          ) &&
          response.mortgageSimulateResponse.reasons.includes(
            'RULE_MORTGAGE_EDAD_MINIMA_TIT_MAX_INGR_REASON_KO',
          )
        ) {
          const index = response.mortgageSimulateResponse.reasons.indexOf(
            'RULE_MORTGAGE_EDAD_MINIMA_TIT_MIN_INGR_REASON_KO',
          );
          response.mortgageSimulateResponse.reasons.splice(index, 1);
        }

        if (
          response.mortgageSimulateResponse.reasons &&
          response.mortgageSimulateResponse.reasons.includes(
            'RULE_MORTGAGE_EDAD_MINIMA_TIT_MIN_INGR_REASON',
          ) &&
          response.mortgageSimulateResponse.reasons.includes(
            'RULE_MORTGAGE_EDAD_MINIMA_TIT_MAX_INGR_REASON',
          )
        ) {
          const index = response.mortgageSimulateResponse.reasons.indexOf(
            'RULE_MORTGAGE_EDAD_MINIMA_TIT_MIN_INGR_REASON',
          );
          response.mortgageSimulateResponse.reasons.splice(index, 1);
        }

        return response;
      }),
    );
  }

  saveAll(bodyData, bodyEconomic) {
    return forkJoin(this.saveData(bodyData), this.saveDataEconomic(bodyEconomic));
  }

  saveAndSimulate(bodyData, bodyEconomic) {
    return this.saveAll(bodyData, bodyEconomic).pipe(
      switchMap(() => this.simulateLevel2()),
    );
  }

  saveData(body: MortgageDataModel) {
    const url = `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/data`;
    return this.commonService.postVerb(url, body);
  }

  saveDataEconomic(body) {
    const url = `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/data/economic`;
    return this.commonService.postVerb(url, body);
  }

  getDocumentType() {
    return this.getParamsByType('DOC_TYPE');
  }
  getRoadType() {
    return this.getParamsByType('ROAD_TYPE');
  }

  getMaritalStatus() {
    return this.getParamsByType('CIVIL_STATUS');
  }

  getEmploymentSituation() {
    return this.getParamsByType('LABORAL_SITUATION');
  }

  getSeniorityCompany() {
    return this.getParamsByType('LABORAL_OLD');
  }

  getApprTypeAssets() {
    return this.getParamsByType('APPR_TYPE_ASSETS');
  }

  getApprRegistry(): Observable<Array<SelectModel>> {
    return this.getParamsByType('APPR_REGISTRY_CODE');
  }

  getActivitySector() {
    return this.getParamsByType('ACTIVITY_SECTOR');
  }

  getAcademicLevel() {
    return this.getParamsByType('ACADEMIC_LEVEL');
  }

  getProfessionalLevel() {
    return this.getParamsByType('PROFESSIONAL_LEVEL');
  }

  getEconomicSystem() {
    return this.getParamsByType('ECONOMIC_SYSTEM');
  }

  getCCAA(): Observable<Array<CCAAModel>> {
    const url = `${environment.apiBase}/mortgages-config-params/${environment.api_version.config_params}/location/regions`;
    return this.commonService.getVerb<Array<CCAAModel>>(url).pipe(
      map((regions) => {
        const index = regions.findIndex((e) => e.id === '999999999');
        if (index > -1) {
          regions.splice(index, 1);
        }
        this.ccaa = regions;
        return regions;
      }),
    );
  }

  getEuriborRequest() {
    const url = `${environment.apiBase}/mortgages-config-params/${environment.api_version.config_params}/config/euribor`;
    return this.http
      .get<Array<SelectModel>>(url, {
        params: new CustomHttpParams(true),
        context: new HttpContext().set(IS_PUBLIC_API, true),
      })
      .pipe(
        map((resp) => {
          const date = resp.find((e) => e.code === 'CURRENT_MONTH').value.split('/');
          return {
            euribor: resp.find((e) => e.code === 'EURIBOR').value,
            currentMonth: date
              ? String(new Date(`${date[1]}/${date[0]}/${date[2]}`).getTime())
              : '-',
            tae: resp.find((e) => e.code === 'TAE').value,
            differential: resp.find((e) => e.code === 'DIFFERENTIAL').value,
            tinY1: resp.find((e) => e.code === 'TINY1').value,
            totalLoan: resp.find((e) => e.code === 'TOTAL_LOAN').value,
            totalInterest: resp.find((e) => e.code === 'TOTAL_INTEREST').value,
            homeInsurance: resp.find((e) => e.code === 'HOME_INSURANCE').value,
          };
        }),
      )
      .subscribe(
        (response) => this.euribor.next(response),
        (_error) => {
          this.euribor.next({
            euribor: '-',
            currentMonth: '-',
            tae: '-',
            differential: '-',
            tinY1: '-',
            totalLoan: '-',
            totalInterest: '-',
            homeInsurance: '-',
            publicationDate: '-',
          });
        },
      );
  }

  getEuribor() {
    return this.euribor;
  }

  sortArray(list: SelectModel[]) {
    list.sort((a: SelectModel, b: SelectModel) => {
      if (a.description < b.description) {
        return -1;
      }
      if (a.description > b.description) {
        return 1;
      }
      return 0;
    });

    const index = list.findIndex(
      (e) => e.description.toLocaleLowerCase().indexOf('otros') > -1,
    );
    if (index > -1) {
      const item = list[index];
      list.splice(index, 1);
      list.push(item);
    }
    return list;
  }

  getPostalCodeFinally() {
    return this.postalCodeFinally.asObservable();
  }

  validatePostalCode(value: string): Observable<any> {
    const url = `${environment.apiBase}/mortgages-config-params/${environment.api_version.config_params}/validate/zipcode/${value}`;
    return this.http
      .get<Array<SelectModel>>(url, {
        context: new HttpContext().set(IS_PUBLIC_API, true),
      })
      .pipe(
        map((resp) => {
          if (resp) {
            return true;
          } else {
            return { noValidPostalCode: true };
          }
        }),
        finalize(() => {
          // hacer next
          this.postalCodeFinally.next(true);
        }),
        catchError((e) => of({ noValidPostalCode: true })),
      );
  }

  public findProvince(id: string): string {
    const ccaa = this.ccaa.find((e) => e.id === id);
    return ccaa ? ccaa.name : '';
  }

  getLifeInsurance(id: string) {
    const url = `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/insurance/validation/${id}`;
    return this.commonService.getVerb(url).pipe(
      map((e) => e.status),
      catchError((e) => of('null')),
    );
  }

  checkLifeInsurance(body: { insuranceConditions: boolean }) {
    const url = `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/insurance/checks`;
    return this.commonService.postVerb(url, body);
  }
}
